import React, { useState, useEffect } from 'react';
import { buildFileUrl } from '../../shared/utils/fileUrl';

interface ProofThumbnailProps {
  /** Stored file path/URL of the proof image (e.g. "/uploads/delivery_proofs/x.png") */
  url?: string | null;
  /** Human label shown as alt text and in the lightbox header */
  label: string;
  className?: string;
}

/**
 * Shows a small thumbnail for an uploaded proof image. Clicking it opens a
 * full-size preview in an in-app lightbox overlay. Renders nothing when there
 * is no image URL (e.g. orders without a payment receipt, or not yet delivered).
 */
const ProofThumbnail: React.FC<ProofThumbnailProps> = ({ url, label, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const src = buildFileUrl(url);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  if (!src) {
    return null;
  }

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="block rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
        title={`View ${label}`}
      >
        <img
          src={src}
          alt={label}
          loading="lazy"
          className="h-20 w-20 rounded border border-gray-200 object-cover hover:opacity-90"
        />
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4"
          onClick={() => setIsOpen(false)}
        >
          <div className="relative max-h-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-white">{label}</span>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="px-2 text-2xl leading-none text-white hover:text-gray-300"
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <img
              src={src}
              alt={label}
              className="max-h-[80vh] max-w-full rounded bg-white object-contain shadow-lg"
            />
            <div className="mt-2 text-center">
              <a
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-300 underline hover:text-blue-200"
              >
                Open original
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProofThumbnail;
